import axios from "axios";

// Константи конфігурації
const API_CONFIG = {
  PRODUCTION_DOMAIN: "api.skyservice.online",
  STAGING_DOMAIN: "staging.api.skyservice.online",
  TIMEOUT: 10000,
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000,
};

const API_ENDPOINTS = {
  GET_START: "/?section=qrmenu&action=getStart",
};

// Визначення середовища
const getEnvironment = () => {
  const hostname = window.location.hostname;

  if (
    process.env.NODE_ENV === "development" ||
    hostname.includes("localhost") ||
    hostname.startsWith("192.168") ||
    hostname.startsWith("10.")
  ) {
    return "development";
  }

  if (hostname.includes("staging")) {
    return "staging";
  }

  return "production";
};

// Отримання базового URL для API
const getApiBaseUrl = () => {
  const env = getEnvironment();
  let hostURL = window.location.host.replace(/\..+/, ".");

  switch (env) {
    case "development":
    case "staging":
      return `https://${API_CONFIG.STAGING_DOMAIN}`;
    case "production":
    default:
      return `https://${hostURL}${API_CONFIG.PRODUCTION_DOMAIN}`;
  }
};

// Створення екземпляру axios з налаштуваннями
const createApiClient = () => {
  return axios.create({
    baseURL: getApiBaseUrl(),
    timeout: API_CONFIG.TIMEOUT,
    headers: {
      "Content-Type": "application/json",
    },
  });
};

// Утиліта для затримки
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Функція retry для запитів
const retryRequest = async (
  requestFn,
  attempts = API_CONFIG.RETRY_ATTEMPTS
) => {
  for (let i = 0; i < attempts; i++) {
    try {
      return await requestFn();
    } catch (error) {
      const isLastAttempt = i === attempts - 1;

      if (isLastAttempt) {
        throw error;
      }

      // Експоненційна затримка
      const delayTime = API_CONFIG.RETRY_DELAY * Math.pow(2, i);
      console.warn(
        `Спроба ${i + 1} не вдалася. Повтор через ${delayTime}мс...`
      );
      await delay(delayTime);
    }
  }
};

// Клас для роботи з API
class ApiService {
  constructor() {
    this.client = createApiClient();
    this.setupInterceptors();
  }

  setupInterceptors() {
    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        // Додавання токену якщо потрібно
        const token = localStorage.getItem("authToken");
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Логування в development mode
        if (process.env.NODE_ENV === "development") {
          console.log("API Request:", config);
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        // Обробка різних типів помилок
        if (error.response) {
          // Сервер відповів з кодом помилки
          console.error("API Error Response:", {
            status: error.response.status,
            data: error.response.data,
          });

          // Обробка специфічних кодів помилок
          switch (error.response.status) {
            case 401:
              // Неавторизований - очистити токен і перенаправити на логін
              localStorage.removeItem("authToken");
              // window.location.href = '/login';
              break;
            case 403:
              console.error("Доступ заборонено");
              break;
            case 404:
              console.error("Ресурс не знайдено");
              break;
            case 500:
              console.error("Внутрішня помилка сервера");
              break;
          }
        } else if (error.request) {
          // Запит був відправлений, але відповідь не отримана
          console.error("No response received:", error.request);
        } else {
          // Щось інше викликало помилку
          console.error("Request setup error:", error.message);
        }

        return Promise.reject(error);
      }
    );
  }

  // Метод для отримання стартових даних
  async getStart(params = {}) {
    const { tpayid, menuId, tradepointId } = params;

    // Валідація параметрів
    // if (!tpayid && !menuId && !tradepointId) {
    //   throw new Error(
    //     "Необхідно вказати хоча б один з параметрів: tpayid, menuId або tradepointId"
    //   );
    // }

    try {
      const response = await retryRequest(async () => {
        return await this.client.get(API_ENDPOINTS.GET_START, {
          params: {
            ...(tpayid && { tpayid }),
            ...(menuId && { menuId }),
            ...(tradepointId && { tradepointId }),
          },
        });
      });

      // Перевірка структури відповіді
      if (!response.data) {
        throw new Error("Порожня відповідь від сервера");
      }

      if (response.data.status !== "done") {
        throw new Error(response.data.message || "Невідома помилка на сервері");
      }

      return response.data.data || null;
    } catch (error) {
      // Детальна обробка помилок
      const errorMessage = this.getErrorMessage(error);
      console.error("Помилка отримання стартових даних:", errorMessage);

      // Можна викинути помилку далі або повернути значення за замовчуванням
      throw new Error(errorMessage);
    }
  }

  // Допоміжний метод для отримання зрозумілого повідомлення про помилку
  getErrorMessage(error) {
    if (error.response?.data?.message) {
      return error.response.data.message;
    }

    if (error.response?.status) {
      const statusMessages = {
        400: "Некоректний запит",
        401: "Необхідна авторизація",
        403: "Доступ заборонено",
        404: "Дані не знайдено",
        500: "Внутрішня помилка сервера",
        502: "Помилка шлюзу",
        503: "Сервіс тимчасово недоступний",
      };

      return (
        statusMessages[error.response.status] ||
        `Помилка сервера: ${error.response.status}`
      );
    }

    if (error.request) {
      return "Немає з'єднання з сервером";
    }

    return error.message || "Невідома помилка";
  }
}

// Створення singleton екземпляру
const apiService = new ApiService();

// Експорт для використання
export default apiService;

// Експорт окремої функції для сумісності зі старим кодом
export const getStart = (params) => apiService.getStart(params);

// Експорт для тестування
export { ApiService, getEnvironment, getApiBaseUrl };
